import { NetworkStatus, useQuery } from "@apollo/client";
import {
  EnforcementIntentsOrderBy,
  GET_ENFORCEMENT_INTENTS_SUMMARY,
  GetEnforcementIntentsSummaryData,
  GetEnforcementIntentsSummaryVars,
  GetEnforcementIntentsWhereFilter,
} from "common/graphql";
import {
  enforcementIntentSimpleStatus,
  EnforcementIntentSummary,
  EnforcementIntentSource,
  enforcementIntentVoidedReasonLabels,
} from "common/graphql/fragments/enforcementIntentSummary";
import {
  parseFilterModelFromSearchParams,
  parseSortModel,
  filterModelToSearchParams,
  serializeSortModel,
} from "common/graphql/queryString";
import {
  DataTableContainer,
  ACTIONS_COLUMN_ICON,
  ACTIONS_COLUMN_TEXT,
  ACTIONS_VIEW_ICON_SX,
  ColumnsToolbarButton,
  ResetLayoutButton,
  FilteredColumnsPanel,
  STANDARD_GRID_PROPS,
  STANDARD_COLUMN_HEADER_TITLE_SX,
  DataGridActionsLinkButton,
} from "@app.automotus.io/components/DataTableContainer";
import {
  DataGridPro,
  getGridDateOperators,
  getGridStringOperators,
  GridActionsColDef,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterInputSingleSelect,
  GridFilterInputValueProps,
  GridFilterModel,
  GridFilterOperator,
  GridSortModel,
  GridToolbarFilterButton,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import React from "react";
import { useSearchParams } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import type { Theme } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import ManualApplyGridFilterPanel from "@app.automotus.io/components/ManualApplyGridFilterPanel";
import { getViolationTypeLabel, getViolationTypeOptions } from "common/format/violationType";
import { formatDwellDuration } from "common/format/time";
import { useApolloClient } from "@apollo/client";
import CustomExportButton from "@app.automotus.io/components/CustomExportButton";
import { TableErrorOverlay } from "@app.automotus.io/components/PayeeTables/TableErrorOverlay";
import { utcToZonedTime } from "date-fns-tz";
import formatDate from "date-fns/format";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useFeatureFlags, useUserProfile } from "@app.automotus.io/components/hooks";
import { getAccountFeatures } from "../../utils/userProfile";
import { FEATURE_FLAGS } from "@curbsuite/constants";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import { convertTimeToString } from "common/format";
import { LiveSessionState, resolveLiveSessionState } from "./Notice/LiveSessionBadge/liveSessionState";
import { ZoneFilterValue, MultiSelectAutocompleteFilterValue } from "./ZoneAreaFilterValue";
import { buildAreaNamesByZoneName, buildNoticesFilter, getAreaNames } from "./data/filterHelpers";
import { useZoneAreaLookups } from "./data/useZoneAreaLookups";

/**
 * The base account-scoped filter for a given Notices tab/variant — "pending" narrows to
 * unreviewed officer-review notices, "all" has no additional restriction. Exported so callers
 * can pass the matching filter to useApplicableViolationTypes and keep the Violation Type
 * filter's options in sync with what each variant's own data query can actually return.
 */
export const getDefaultFilterByVariant = (variant: NoticeSummaryTableVariant): GetEnforcementIntentsWhereFilter => {
  return variant === "pending"
    ? {
        status: { _eq: "requires_officer_review" },
        reviews: { type: { _eq: "officer" }, reviewed_at: { _is_null: true } },
      }
    : {};
};

/**
 * Keeps a currently-applied Violation Type filter value selectable even when it falls outside
 * the tab's applicable set — otherwise MUI's GridFilterInputSingleSelect sanitizes it away on
 * mount (e.g. a bookmarked/shared URL restoring a filter that's valid on a different tab),
 * silently widening the grid from a filtered view to all rows with no indication.
 */
export const withCurrentFilterValue = (
  options: { value: string; label: string }[],
  currentValue: string | undefined,
  accountId?: string,
): { value: string; label: string }[] => {
  if (!currentValue || options.some((option) => option.value === currentValue)) {
    return options;
  }
  return [...options, { value: currentValue, label: getViolationTypeLabel(currentValue, accountId) }];
};

/**
 * Returns the named string filter operators in the order given, rather than MUI's own.
 *
 * Two reasons the order has to be declared rather than inherited. MUI preselects whichever
 * operator is first in the list, and `getGridStringOperators()` puts `contains` first - so a
 * plain `.filter()` over the built-in list keeps `contains` as the default no matter which
 * operators are kept.
 *
 * And restricting the list is a correctness fix, not only a default-value one: the filter
 * builder below maps `equals` to `_eq` and *everything else* to `_ilike '%value%'`. Left
 * unrestricted, `startsWith` and `endsWith` silently return contains-matches, and `isAnyOf`
 * stringifies its array into one `_ilike '%a,b%'` that matches nothing. (`isEmpty` and
 * `isNotEmpty` never arrive - `filterModelToSearchParams` drops value-less items.) Only the
 * operators listed here are ones the builder actually honours.
 *
 * Misspelling a name drops it, same as the `.filter()` this replaces - it can never invent an
 * operator, only return a subset of the real ones.
 */
export const orderedStringOperators = (
  values: string[],
): GridFilterOperator<EnforcementIntentSummary, string, string>[] =>
  getGridStringOperators()
    .filter((operator) => values.includes(operator.value))
    .sort((a, b) => values.indexOf(a.value) - values.indexOf(b.value)) as GridFilterOperator<
    EnforcementIntentSummary,
    string,
    string
  >[];

/**
 * `licensePlateRegion` is a nullable "us-xx" code; the leading "us-" is a fixed prefix so only
 * the state code after it is ever shown. Falls back to the bare plate number when the region is
 * missing, rather than throwing, since a plate can be recorded without a region.
 */
export const formatLicensePlate = (licensePlateRegion: string | null, licensePlateNumber: string): string => {
  const region = licensePlateRegion?.split("-")[1]?.toUpperCase();
  return region ? `${region} ${licensePlateNumber}` : licensePlateNumber;
};

// Shared by sourceColumn's valueGetter and renderCell below, so CSV export and the on-screen
// Chip always agree -- valueGetter is what CSV export actually reads (it never calls renderCell,
// which returns a React node, not an exportable value).
export const sourceLabel = (source: EnforcementIntentSource): string =>
  source === "officer_input" ? "Manual" : "Camera";

// On mobile, both tabs default to a narrower set of columns -- Plate, Zone, and Issue Date --
// rather than the full desktop column set, which doesn't fit a phone-width grid without heavy
// horizontal scrolling. The rest stay toggleable via the Columns panel.
const MOBILE_NOTICES_DEFAULT_VISIBLE_FIELDS = new Set(["licensePlate", "zone", "issuedAt"]);

const NO_AREA_NAMES = new Map<string, string[]>();

export const NoticeSummaryTable: React.FC<NoticeSummaryTableProps> = ({
  variant,
  onChangeVariant,
  applicableViolationTypes,
}) => {
  const client = useApolloClient();
  // noSsr: true -- this app is CSR-only (no hydration to protect), and without it MUI's
  // useMediaQuery always reports `false` on the very first render before updating to the real
  // value in an effect. That first (always-desktop) value is what DataTableContainer's
  // defaultVisibilityModel prop gets baked into on mount (see its own initialState useState,
  // which only runs once) -- so a mobile viewer's real default column set was never actually
  // applied, even though the key={isMobile ? "mobile" : "desktop"} below does force the grid
  // itself to remount once isMobile catches up.
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"), { noSsr: true });
  const { userProfile } = useUserProfile();

  const isAirport = userProfile?.account?.ownerType === "airport";

  const {
    isBusStation,
    notices: { isTimeDisplayEnabledForPendingNotices },
  } = getAccountFeatures(userProfile);
  const { isEnabled } = useFeatureFlags();
  // Per-client gate, resolved server-side from feature_flag + feature_flag_exclusion, so the
  // set of clients seeing the Dwell column can change without a redeploy.
  const isDwellColumnEnabled = isEnabled(FEATURE_FLAGS.PENDING_NOTICES_DWELL_COLUMN);
  // Same server-resolved per-client gate as Dwell (AUT-5763). Held off by default
  // until CV-2333 lands: ~7% of parks never close observationally, and a stale-open
  // park renders as a tow-eligible row. The amber state below makes that population
  // visible rather than silent, but the flag is what keeps it away from officers
  // until the underlying rate is fixed.
  const isTowEligibleColumnEnabled = isEnabled(FEATURE_FLAGS.PENDING_NOTICES_TOW_ELIGIBLE_COLUMN);
  // Same per-account flag Notices/GenerateCitation gate the create flow behind -- the Source
  // column is only meaningful once officer-submitted citations can actually exist.
  const isManualCitationsFeatureEnabled = isEnabled(FEATURE_FLAGS.MANUAL_CITATIONS_IS_ENABLED);

  // When the rows on screen were last confirmed over the network. The dot is a
  // dispatch signal, so it is stamped with a time we actually observed rather
  // than with `new Date()` at render — the same honesty rule as the review page's
  // badge (AUT-5502). null until the first response completes.
  //
  // Held twice on purpose. The ref is what the Tow-Eligible column reads: keeping
  // it out of TABLE_COLUMNS' dependencies stops all ten column definitions being
  // rebuilt on every 10s poll tick and handed to an apiRef-controlled DataGridPro
  // as a new reference. The state drives the toolbar's staleness label, which has
  // to re-render to stay truthful. Both are written together below.
  const liveDataAsOfRef = React.useRef<Date | null>(null);
  const [liveDataAsOf, setLiveDataAsOf] = React.useState<Date | null>(null);

  // The dots age off the wall clock (see resolveTowEligibility), but nothing
  // repaints them without a render. A completed poll provides that every 10s;
  // when polling is paused there is no such trigger, so the red→amber crossing
  // would be computed correctly and simply never drawn. This ticks only in that
  // state, and only when the column is on screen — no network call, and no
  // effect on TABLE_COLUMNS' identity, since it re-renders the component rather
  // than rebuilding the column definitions. Sixty seconds is well inside the
  // two-hour threshold it exists to cross.
  const [, setAgeTick] = React.useState(0);

  const variantDefaults = PARAMS_BY_VARIANT[variant];
  const defaultSortColumn = variantDefaults.defaultSortColumn;
  const defaultSortOrder =
    variant == "pending" && userProfile?.account.id === process.env.REACT_APP_SMF_ACCOUNT_ID
      ? "desc"
      : variantDefaults.defaultSortOrder;
  const defaultSort = `${defaultSortColumn}+${defaultSortOrder}`;

  const [searchParams, setSearchParams] = useSearchParams({
    page: "1",
    sort: defaultSort,
  });

  // Carried on each row's detail link so its own back button can restore this exact list view —
  // including page/sort/filter — even when the link is opened in a new tab, where react-router's
  // `state` (passed separately below) never arrives: a fresh tab has no access to the originating
  // tab's in-memory history state, only to whatever is in the URL itself.
  const returnTo = `/notices?${searchParams.toString()}`;

  // Computed up here (rather than alongside the rest of the filter/search-param state below) so
  // TABLE_COLUMNS can keep a URL-restored Violation Type filter value selectable — see
  // withCurrentFilterValue's doc comment.
  const appliedFilters = React.useMemo(() => parseFilterModelFromSearchParams(searchParams), [searchParams]);

  const zoneAreaLookups = useZoneAreaLookups({
    payeeAccountId: userProfile?.account?.id,
    isAirport,
    appliedFilters,
  });

  // Distinct from a genuinely empty result -- an errored request also leaves areaOptions as [],
  // which would otherwise render as an indistinguishable "No options" with no way for an officer
  // to tell why they can't select an area.
  const areaOptionsFailedText = zoneAreaLookups.curbAreasError ? "Couldn't load areas -- try refreshing" : undefined;

  const areaOptions = React.useMemo(
    () => zoneAreaLookups.curbAreas.map((area) => ({ value: area.id, label: area.name })),
    [zoneAreaLookups.curbAreas],
  );

  const currentViolationTypeFilterValue = appliedFilters?.items?.find(
    (item) => item.columnField === "violationType" && item.operatorValue === "equals",
  )?.value;

  const page = Number(searchParams.get("page") || 1);

  // Poll for new pending notices every 10s, but only when the user is on the
  // first page with no column filters applied. Polling on deeper pages would
  // shift rows mid-read, and filtered views should stay stable.
  //
  // Declared above TABLE_COLUMNS rather than next to the query because the
  // Tow-Eligible column's own gate reads `variant`, and the toolbar's pause chip
  // reads `shouldPoll` — both are resolved before the columns are built.
  const hasColumnFilters = appliedFilters?.items?.some((item) => item.value) ?? false;
  const shouldPoll = variant === "pending" && page === 1 && !hasColumnFilters;

  // Mirrors the mobile column-list branch below, which never includes towEligibleColumn —
  // without this, the toolbar's staleness chip (and the age-tick interval it drives) could
  // still show/run on mobile even though there's no such column on screen to reference.
  const isTowEligibleColumnShown = variant === "pending" && isTowEligibleColumnEnabled && !isMobile;
  const needsAgeTick = isTowEligibleColumnShown && !shouldPoll;
  React.useEffect(() => {
    if (!needsAgeTick) {
      return;
    }
    const id = window.setInterval(() => setAgeTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, [needsAgeTick]);

  const TABLE_COLUMNS = React.useMemo(() => {
    const noticeNumberColumn: GridColDef<EnforcementIntentSummary, string> = {
      field: "noticeNumber",
      headerName: "Notice Number",
      align: "left",
      headerAlign: "left",
      sortable: false,
      // Exact match only. A notice number is an identifier people paste in whole, and every
      // other operator on this column was either wrong or slow - see orderedStringOperators
      // and AUT-5789. `contains` was the inherited default, which made the slowest and least
      // useful option the one a user got by opening the filter.
      filterOperators: orderedStringOperators(["equals"]),
      flex: 1,
    };

    const violationTypeColumn: GridColDef<EnforcementIntentSummary, string> = {
      field: "violationType",
      headerName: "Violation Type",
      align: "left",
      headerAlign: "left",
      type: "singleSelect",
      valueOptions: withCurrentFilterValue(
        getViolationTypeOptions(userProfile?.account?.id, applicableViolationTypes),
        currentViolationTypeFilterValue,
        userProfile?.account?.id,
      ),
      sortable: false,
      filterable: true,
      filterOperators: getGridStringOperators()
        .filter((operator) => operator.value === "equals")
        .map((operator) => ({
          ...operator,
          InputComponent: operator.InputComponent ? GridFilterInputSingleSelect : undefined,
        })) as GridFilterOperator<EnforcementIntentSummary, string, string>[],
      valueGetter({ row }) {
        return getViolationTypeLabel(row.violationType, userProfile?.account?.id);
      },
      flex: 1,
    };

    const licensePlateColumn: GridColDef<EnforcementIntentSummary, string> = {
      field: "licensePlate",
      headerName: "Plate",
      align: "left",
      headerAlign: "left",
      ...(isMobile ? { width: 106 } : { flex: 1 }),
      valueGetter({ row }) {
        return formatLicensePlate(row.licensePlateRegion, row.licensePlateNumber);
      },
      sortable: false,
      // `contains` stays - a partial plate is a real lookup, unlike a partial notice number -
      // but `equals` leads so the exact match is what opening the filter gives you.
      filterOperators: orderedStringOperators(["equals", "contains"]),
    };

    const issuedAtColumn: GridColDef<EnforcementIntentSummary, Date, string> = {
      field: "issuedAt",
      headerName: "Issue Date",
      type: "date",
      ...(isMobile ? { width: 108 } : { flex: 1 }),
      valueGetter({ row }) {
        return utcToZonedTime(row.issuedAt, row.timezone);
      },
      valueFormatter({ value }) {
        return isTimeDisplayEnabledForPendingNotices
          ? isMobile
            ? formatDate(value, "HH:mm:ss")
            : value.toLocaleString()
          : value.toLocaleDateString();
      },
      sortingOrder: ["desc", "asc"],
      filterOperators: getGridDateOperators().filter((op) =>
        ["is", "before", "after"].includes(op.value),
      ) as GridFilterOperator<EnforcementIntentSummary, Date, string>[],
    };

    const locationColumn: GridColDef<EnforcementIntentSummary, string> = {
      field: "location",
      headerName: "Address",
      type: "string",
      flex: 1,
      valueGetter({ row }) {
        return row.sessions.length > 0 ? row.sessions[0].streetAddress : "-";
      },
      sortable: false,
      filterable: false,
    };

    const zoneColumn: GridColDef<EnforcementIntentSummary, string> = {
      field: "zone",
      headerName: "Zone",
      align: "left",
      headerAlign: "left",
      sortable: false,
      filterable: true,
      flex: 1,
      valueGetter({ row }) {
        return row.sessions[0]?.streetAddress || "-";
      },
      filterOperators: [
        {
          value: "equals",
          // Never actually invoked -- the grid is configured with filterMode="server"
          // (see the DataGridPro props further down this file), so DataGridPro never runs
          // client-side re-filtering over already-server-filtered rows. If that ever changes,
          // this MUST be replaced with a real predicate, since valueGetter returns the zone's
          // *name* while `item.value` here is a zone *id* -- a naive client-side re-check would
          // silently filter out every row.
          getApplyFilterFn: () => null,
          InputComponent: (props: GridFilterInputValueProps) => (
            <ZoneFilterValue {...props} payeeAccountId={userProfile?.account?.id ?? ""} />
          ),
        },
      ] as GridFilterOperator<EnforcementIntentSummary, string, string>[],
    };

    const areaColumn: GridColDef<EnforcementIntentSummary, string> = {
      field: "area",
      headerName: "Area",
      align: "left",
      headerAlign: "left",
      sortable: false,
      filterable: true,
      flex: 1,
      valueGetter: ({ row }) => getAreaNames(row, zoneAreaLookups.areaNamesByZoneName),
      filterOperators: [
        {
          // "isAnyOf", not "equals" -- an officer can be assigned more than one coverage area
          // at once (AUT-5863), so this filter is multi-select. item.value is a comma-joined
          // string of selected area ids (see MultiSelectAutocompleteFilterValue's own comment
          // on why -- this app's filter-URL persistence can't round-trip a real array).
          value: "isAnyOf",
          // Same rationale as zoneColumn above -- never invoked under filterMode="server".
          getApplyFilterFn: () => null,
          InputComponent: (props: GridFilterInputValueProps) => (
            <MultiSelectAutocompleteFilterValue
              {...props}
              label="Area"
              options={areaOptions}
              noOptionsText={areaOptionsFailedText}
            />
          ),
        },
      ] as GridFilterOperator<EnforcementIntentSummary, string, string>[],
    };

    const statusColumn: GridColDef<EnforcementIntentSummary, string> = {
      field: "status",
      headerName: "Status",
      align: "left",
      headerAlign: "left",
      type: "singleSelect",
      valueOptions: ["pending", "issued", "voided"],
      flex: 1,
      sortable: false,
      filterable: true,
      filterOperators: getGridStringOperators()
        .filter((op) => ["equals"].includes(op.value))
        .map((operator) => ({
          ...operator,
          InputComponent: operator.InputComponent ? GridFilterInputSingleSelect : undefined,
        })) as GridFilterOperator<EnforcementIntentSummary, string, string>[],
      valueGetter({ row }) {
        return enforcementIntentSimpleStatus[row.status];
      },
      valueFormatter({ value }) {
        return value.charAt(0).toUpperCase() + value.slice(1);
      },
    };

    const actionsColumn: GridActionsColDef = {
      ...(variant === "pending" ? ACTIONS_COLUMN_TEXT : ACTIONS_COLUMN_ICON),
      getActions: (params) =>
        variant === "pending"
          ? [
              <DataGridActionsLinkButton
                to={`${params.row.id}?returnTo=${encodeURIComponent(returnTo)}`}
                state={{ from: "notice-summary" }}
              >
                Review
              </DataGridActionsLinkButton>,
            ]
          : [
              <DataGridActionsLinkButton
                to={`${params.row.id}?returnTo=${encodeURIComponent(returnTo)}`}
                state={{ from: "notice-summary" }}
                label={"View Citation"}
                icon={<ArrowForwardIosIcon sx={ACTIONS_VIEW_ICON_SX} />}
              />,
            ],
    } as GridActionsColDef;

    const sourceColumn: GridColDef<EnforcementIntentSummary, string> = {
      field: "source",
      headerName: "Source",
      width: 100,
      sortable: false,
      filterable: false,
      // CSV export reads valueGetter/valueFormatter, never renderCell (which returns a React
      // node, not an exportable value) -- without this, an exported report would show the raw
      // "officer_input"/"automated" enum values instead of the "Manual"/"Camera" labels the
      // table itself displays.
      valueGetter({ row }) {
        return sourceLabel(row.source);
      },
      renderCell: ({ row }) =>
        row.source === "officer_input" ? (
          <Chip label="Manual" color="info" size="small" />
        ) : (
          // "default" (a neutral grey) rather than a more saturated color like success/warning --
          // those imply a status judgment (camera-detected is neither good nor bad), and picking
          // one would also draw more attention than the far more common automated row should get.
          <Chip label="Camera" color="default" size="small" />
        ),
    };

    const voidedReasonColumn: GridColDef<EnforcementIntentSummary, string> = {
      field: "voidedReason",
      headerName: "Void Reason",
      align: "left",
      headerAlign: "left",
      sortable: false,
      filterable: false,
      valueGetter({ row }) {
        if (!row.voidedReason) {
          return "N/A";
        }

        if (row.voidedReason === "officer_review") {
          return row.reviews.find((r) => r.type === "officer")?.deniedReason ?? "N/A";
        }

        return enforcementIntentVoidedReasonLabels[row.voidedReason] ?? "N/A";
      },
      valueFormatter({ value }) {
        return value
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      },
      flex: 1.5,
    };

    // Display-only dwell column for the Pending Notices tab. Server-side sort/filter is
    // intentionally omitted here due to a known DB indexing/performance blocker, so it is
    // sortable: false / filterable: false.
    const dwellColumn: GridColDef<EnforcementIntentSummary, string> = {
      field: "dwell",
      headerName: "Dwell",
      align: "left",
      headerAlign: "left",
      sortable: false,
      filterable: false,
      flex: 1,
      valueGetter({ row }) {
        // sessions?.[0]: every enforcement_intent currently has exactly one session, per the
        // ticket's own open question — checked against prod, not assumed.
        const park = row.sessions?.[0]?.park;
        return park?.endTime != null && park.durationSec != null ? formatDwellDuration(park.durationSec) : "-";
      },
    };

    // Tow-Eligible (AUT-5763). Three states, inherited verbatim from the review
    // page badge via the shared resolveLiveSessionState so the two surfaces
    // cannot disagree: red = open ≤2h, amber = open >2h ("unverified", most
    // likely a CV-2333 stale-open park), blank = closed or no readable event.
    //
    // sortable: false is deliberate, not an oversight. `sessions` is a Hasura
    // ARRAY relationship, so enforcement_intent_order_by cannot reach through it
    // to park.end_time at all — server-side sorting needs a scalar computed field
    // or a denormalised column on enforcement_intent. The Dwell column above hit
    // the same wall on the same data path and dropped sorting for a known DB
    // indexing/performance blocker. Sorting client-side would only order the
    // current 100-row page and quietly mislead, so it is left to its own ticket.
    // Filtering IS pushed down — see the towEligible branch in `filter` below.
    const towEligibleColumn: GridColDef<EnforcementIntentSummary, string> = {
      field: "towEligible",
      headerName: "Tow-Eligible",
      align: "center",
      headerAlign: "center",
      width: 132,
      sortable: false,
      filterable: true,
      type: "singleSelect",
      valueOptions: ["Yes", "No"],
      filterOperators: getGridStringOperators()
        .filter((operator) => operator.value === "equals")
        .map((operator) => ({
          ...operator,
          InputComponent: operator.InputComponent ? GridFilterInputSingleSelect : undefined,
        })) as GridFilterOperator<EnforcementIntentSummary, string, string>[],
      // `description` rather than a custom renderHeader. MUI renders one or the
      // other: supplying renderHeader replaces GridColumnHeaderTitle entirely
      // (GridColumnHeaderItem.js:194), and that is the element carrying the
      // .MuiDataGrid-columnHeaderTitle class that STANDARD_COLUMN_HEADER_TITLE_SX
      // styles at 18px/700 — so a custom header silently renders at default
      // weight while every neighbouring column is bold. `description` keeps the
      // standard markup and still tooltips unconditionally (GridColumnHeaderTitle
      // passes `description || tooltip` straight to BaseTooltip, so unlike the
      // overflow fallback it does not wait for the label to be truncated).
      //
      // Whether the view is refreshing is stated by the toolbar's always-visible
      // chip, not here — a hover-only tooltip is the silent case AUT-5763 says
      // not to ship. The per-row tooltip still carries the exact as-of time,
      // because that renders in the notice's own timezone and only a row knows it.
      description:
        "Red: vehicle still at this location. Amber: session open over two hours, so presence is unverified. Blank: not tow-eligible. Hover a dot for the time it was last confirmed.",
      // "Yes" covers both the red and amber states: open-vs-closed is what the
      // server filter and the CSV can express, while the red/amber distinction is
      // a rendering nuance that matters on screen, where the dispatch decision is
      // actually made. Returning "No" rather than "" keeps the export unambiguous
      // — a blank CSV cell reads as "unknown" — while renderCell still leaves the
      // on-screen cell empty, per the mockup.
      valueGetter({ row }) {
        return resolveTowEligibility(row, liveDataAsOfRef.current !== null).kind === "none" ? "No" : "Yes";
      },
      renderCell({ row }) {
        const asOf = liveDataAsOfRef.current;
        const state = resolveTowEligibility(row, asOf !== null);
        if (!asOf || state.kind === "none") {
          return null;
        }
        const isFresh = state.kind === "fresh";
        return (
          <Tooltip
            title={
              isFresh
                ? `Still in violation · as of ${convertTimeToString(utcToZonedTime(asOf, row.timezone))}`
                : `Unverified — session open ${state.openHours}h`
            }
          >
            <Box
              aria-label={isFresh ? "Still in violation" : "Unverified, session open over two hours"}
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: (theme: Theme) => (isFresh ? theme.palette.error.main : theme.palette.warning.main),
              }}
            />
          </Tooltip>
        );
      },
    };

    const columns = [
      noticeNumberColumn,
      violationTypeColumn,
      licensePlateColumn,
      locationColumn,
      zoneColumn,
      ...(isAirport ? [areaColumn] : []),
      issuedAtColumn,
      ...(variant === "pending" && isDwellColumnEnabled ? [dwellColumn] : []),
      ...(isTowEligibleColumnShown ? [towEligibleColumn] : []),
      ...(variant === "all" ? [statusColumn, voidedReasonColumn] : []),
      ...(isManualCitationsFeatureEnabled && variant === "all" ? [sourceColumn] : []),
    ];

    // Mobile Pending reviews from the detail panel's button instead of the actions column.
    if (isMobile && variant === "pending") {
      return [
        ...columns,
        {
          ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
          renderHeader: () => null,
          width: 0,
          flex: 0,
          minWidth: 0,
          headerName: "Details",
          hideable: false,
        },
      ];
    }

    return [...columns, actionsColumn];
  }, [
    variant,
    isMobile,
    isTimeDisplayEnabledForPendingNotices,
    userProfile?.account?.id,
    isAirport,
    applicableViolationTypes,
    currentViolationTypeFilterValue,
    isDwellColumnEnabled,
    returnTo,
    isTowEligibleColumnShown,
    isManualCitationsFeatureEnabled,
    areaOptions,
    areaOptionsFailedText,
    zoneAreaLookups.areaNamesByZoneName,
  ]);

  // Export loads its own curb areas, since the table's may not have loaded yet (AUT-6037). Filled by
  // getExportFilter; areas depend only on the account, so overlapping exports fill in the same lookup.
  const exportAreaNamesByZoneNameRef = React.useRef(NO_AREA_NAMES);

  // Same columns as the grid, plus Carrier for the export only (AUT-5661), gated to the PPABUS
  // account (isBusStation) since Carrier only has meaning for that client's operator-billed
  // citations. Kept separate from TABLE_COLUMNS/defaultVisibilityModel so Carrier never appears
  // in the grid or its Columns toggle panel.
  const EXPORT_COLUMNS = React.useMemo(() => {
    const columns = TABLE_COLUMNS.map((column) =>
      column.field === "area"
        ? {
            ...column,
            valueGetter: ({ row }: { row: EnforcementIntentSummary }) =>
              getAreaNames(row, exportAreaNamesByZoneNameRef.current),
          }
        : column,
    );
    return isBusStation ? [...columns, carrierColumn] : columns;
  }, [TABLE_COLUMNS, isBusStation]);

  // Derived from TABLE_COLUMNS itself (rather than a separately hand-maintained constant) so it
  // always matches this render's actual column set exactly — including which variant/mobile
  // state it's for. A hand-maintained list can drift out of sync with real columns (a stale or
  // missing key silently breaks DataTableContainer's saved-layout column-order reconciliation)
  // and, shared verbatim across variants with genuinely different columns, can seed a column that
  // doesn't exist for a given variant.
  const defaultVisibilityModel: GridColumnVisibilityModel = React.useMemo(() => {
    if (isMobile) {
      return Object.fromEntries(
        TABLE_COLUMNS.map((column) => [
          column.field,
          // A column with hideable: false (the actions column) can't actually be hidden by
          // DataGridPro, but it still stores whatever this model says for it in its live
          // columnVisibilityModel state -- marking it false here would make useHiddenColumnsCount
          // see it as "hidden" (inflating the Columns toolbar button's badge count) even though
          // it's visually still there. Treating it as always visible keeps this model and
          // shippedDefaultVisibilityModel below agreeing on it.
          column.hideable === false || MOBILE_NOTICES_DEFAULT_VISIBLE_FIELDS.has(column.field),
        ]),
      );
    }
    return Object.fromEntries(TABLE_COLUMNS.map((column) => [column.field, true]));
  }, [TABLE_COLUMNS, isMobile]);

  // useHiddenColumnsCount's badge counts columns hidden relative to a fixed, code-defined
  // default -- never derive its baseline from a value affected by device/state, or every column
  // this table hides "by default" becomes permanently invisible to the counter (see that hook's
  // own doc comment). `defaultVisibilityModel` above is exactly such a value on mobile: it's the
  // mount-time seed for what to hide by default there, so using it as the badge's baseline too
  // would mean the badge could never show on mobile, even with several columns actually hidden.
  // This always-visible model gives the badge the full desktop set as its baseline instead, so it
  // correctly counts the mobile-hidden columns as "hidden."
  const shippedDefaultVisibilityModel: GridColumnVisibilityModel = React.useMemo(
    () => Object.fromEntries(TABLE_COLUMNS.map((column) => [column.field, true])),
    [TABLE_COLUMNS],
  );

  const handlePageChange = (newPage: number) => {
    const newPageOneIndexed = newPage + 1;

    if (newPageOneIndexed !== page) {
      searchParams.set("page", `${newPageOneIndexed}`);
      setSearchParams(searchParams, { replace: true });
    }
  };

  const PAGE_SIZE = 100;

  const sort = searchParams.get("sort") || defaultSort;
  const sortModel: GridSortModel = React.useMemo(() => parseSortModel(sort), [sort]);
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    searchParams.set("sort", serializeSortModel(newSortModel));
    searchParams.set("page", "1");
    setSearchParams(searchParams, { replace: true });
  };
  const orderBy: EnforcementIntentsOrderBy[] = React.useMemo(() => {
    const { field: sortField, sort: sortDirection } = sortModel[0];
    switch (sortField) {
      case "issuedAt":
        return [{ issued_at: sortDirection || "asc" }];
      default:
        return [{ issued_at: sortDirection || "asc" }];
    }
  }, [sortModel]);

  const [filterModel, setFilterModel] = React.useState<GridFilterModel | undefined>(appliedFilters);

  const handleApplyFilters = (newFilterModel: GridFilterModel) => {
    searchParams.set("page", "1");
    const filterParams = filterModelToSearchParams(newFilterModel, searchParams);
    setSearchParams(filterParams, { replace: true });
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    if (newFilterModel.items.length === 0 && filterModel?.items?.length) {
      // Manually apply filters when user clears all filters
      handleApplyFilters(newFilterModel);
    }

    if (newFilterModel !== filterModel) {
      setFilterModel(newFilterModel);
    }
  };

  const filter = React.useMemo(
    () =>
      buildNoticesFilter(
        getDefaultFilterByVariant(variant),
        appliedFilters,
        zoneAreaLookups.zoneNamesById,
        zoneAreaLookups.curbAreas,
      ),
    [variant, appliedFilters, zoneAreaLookups.zoneNamesById, zoneAreaLookups.curbAreas],
  );

  const { data, error, networkStatus, refetch } = useQuery<
    GetEnforcementIntentsSummaryData,
    GetEnforcementIntentsSummaryVars
  >(GET_ENFORCEMENT_INTENTS_SUMMARY, {
    variables: {
      offset: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      orderBy,
      filter,
    },
    fetchPolicy: "network-only",
    // A Zone or Area filter can't match anything until its names load; if they fail, the error overlay shows instead.
    skip: zoneAreaLookups.isLoading || zoneAreaLookups.error !== undefined,
    pollInterval: shouldPoll ? 10_000 : undefined,
    // Load-bearing for the stamp below, not cosmetic. Apollo 3.13.8 fires
    // onCompleted only on a networkStatus *transition* into `ready`
    // (react/hooks/useQuery.js). Without this flag the intermediate poll
    // status is never emitted, so every poll result arrives as ready-after-
    // ready, the guard fails, and onCompleted runs exactly once on first
    // load. Notice.tsx:98 sets it for this same stamp. See `loading` below.
    notifyOnNetworkStatusChange: true,
    // Stamps the Tow-Eligible dot with a time we actually observed. Safe to
    // set on every completion here because fetchPolicy is network-only, so
    // each emission really is a network response — unlike the review page,
    // which is cache-and-network and has to distinguish the two (AUT-5763).
    onCompleted: () => {
      const at = new Date();
      liveDataAsOfRef.current = at;
      setLiveDataAsOf(at);
    },
  });

  // notifyOnNetworkStatusChange above makes polls and refetches flip `loading`
  // too, which would flash the grid's overlay every 10s. Only a fetch with no
  // data yet — first load, or new variables after a page/filter change — should
  // show it. Same narrowing as Notice.tsx:105, which hit this first.
  const loading =
    zoneAreaLookups.isLoading ||
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables;

  // Built per call from this render's filters, so overlapping exports can't use each other's filter.
  const getExportFilter = async () => {
    const { zoneNamesById, curbAreas } = await zoneAreaLookups.loadForExport();
    exportAreaNamesByZoneNameRef.current = buildAreaNamesByZoneName(curbAreas);
    return buildNoticesFilter(getDefaultFilterByVariant(variant), appliedFilters, zoneNamesById, curbAreas);
  };

  // Retries whichever lookup failed, or the notices query itself.
  const handleTryAgain = () => {
    if (zoneAreaLookups.error) {
      zoneAreaLookups.retry();
    } else {
      refetch();
    }
  };

  // Functions for custom export
  const fetchTotalCount = async () => {
    const result = await client.query<GetEnforcementIntentsSummaryData, GetEnforcementIntentsSummaryVars>({
      query: GET_ENFORCEMENT_INTENTS_SUMMARY,
      variables: {
        offset: 0,
        limit: 0,
        orderBy,
        filter: await getExportFilter(),
      },
    });
    return result.data?.enforcementIntentAggregate.aggregate.count || 0;
  };

  const fetchDataBatch = async (offset: number, limit: number) => {
    const result = await client.query<GetEnforcementIntentsSummaryData, GetEnforcementIntentsSummaryVars>({
      query: GET_ENFORCEMENT_INTENTS_SUMMARY,
      variables: {
        offset,
        limit,
        orderBy,
        filter: await getExportFilter(),
      },
    });
    return result.data?.enforcementIntents || [];
  };

  // Detail panel for mobile pending notices
  const getDetailPanelContent = React.useCallback(
    ({ row }: { row: EnforcementIntentSummary }) => {
      if (isMobile && variant === "pending") {
        return (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 0.5, px: 0.5 }}>
            <DataGridActionsLinkButton
              to={`${row.id}?returnTo=${encodeURIComponent(returnTo)}`}
              state={{ from: "notice-summary" }}
              variant="contained"
              fullWidth
              sx={{ textTransform: "none" }}
            >
              Review
            </DataGridActionsLinkButton>
          </Stack>
        );
      }
      return null;
    },
    [isMobile, variant, returnTo],
  );

  const getDetailPanelHeight = React.useCallback(() => {
    return 48;
  }, []);

  const detailPanelExpandedRowIds = React.useMemo(() => {
    if (isMobile && variant === "pending") {
      return data?.enforcementIntents.map((row) => row.id) || [];
    }
    return [];
  }, [isMobile, variant, data?.enforcementIntents]);

  return (
    <DataTableContainer
      // key={isMobile ...}: DataTableContainer's initialState (and the pagehide/visibilitychange
      // listeners that later save over it) are seeded once per mount from defaultVisibilityModel
      // and storageKey, same as Notices.tsx's key={tab} above forces a remount on variant switch
      // for the exact same reason. Without also keying on isMobile here, resizing from mobile to
      // desktop mid-session (no page reload) leaves the grid showing the stale mobile-narrowed
      // initialState even though tableName/defaultVisibilityModel have already updated to their
      // desktop values -- and a subsequent page refresh's pagehide save then persists that stale,
      // still-3-column state under the (by-then-current) desktop storageKey, permanently
      // poisoning the desktop layout. Keying here forces a full remount on every isMobile flip, so
      // initialState is always recomputed fresh against the viewport that's actually active.
      key={isMobile ? "mobile" : "desktop"}
      // Scoped by variant, not just account — the "pending" and "all" tabs have different columns.
      // Each variant also gets its own mobile-specific key: mobile and desktop otherwise
      // share one saved layout, and DataTableContainer's restored columnVisibilityModel (if one
      // has ever been saved, on either device) always wins over defaultVisibilityModel -- so a
      // user with an existing desktop layout would never actually see the mobile-only default
      // (MOBILE_NOTICES_DEFAULT_VISIBLE_FIELDS above), and toggling columns on mobile would
      // silently overwrite their desktop layout the next time they visit on a phone. Splitting the
      // key gives each viewport its own independent saved state, seeded from its own default.
      tableName={`notices-table-${variant}${isMobile ? "-mobile" : ""}`}
      defaultVisibilityModel={defaultVisibilityModel}
    >
      {({ apiRef, initialState, isIconOnly, resetLayout }) => (
        <DataGridPro
          apiRef={apiRef}
          key={isMobile ? "mobile" : "desktop"}
          localeText={isIconOnly ? { toolbarFilters: "" } : undefined}
          components={{
            Toolbar: TableToolbar,
            FilterPanel: ManualApplyGridFilterPanel,
            ColumnsPanel: FilteredColumnsPanel,
            ErrorOverlay: TableErrorOverlay,
          }}
          componentsProps={{
            filterPanel: {
              onApplyFilters: handleApplyFilters,
            },
            errorOverlay: {
              onTryAgain: handleTryAgain,
            },
            toolbar: {
              tab: variant,
              onChangeTab: onChangeVariant,
              fetchTotalCount,
              fetchDataBatch,
              exportFileName: `notices_${variant}_${Date.now()}`,
              columns: EXPORT_COLUMNS,
              defaultVisibilityModel: shippedDefaultVisibilityModel,
              isIconOnly,
              resetLayout,
              isTowEligibleColumnShown,
              isAutoRefreshing: shouldPoll,
              liveDataAsOf,
            },
          }}
          columns={TABLE_COLUMNS}
          rows={data?.enforcementIntents || []}
          rowCount={data?.enforcementIntentAggregate.aggregate.count || 0}
          loading={loading}
          {...STANDARD_GRID_PROPS}
          error={zoneAreaLookups.error ?? error}
          pagination
          page={page - 1}
          onPageChange={handlePageChange}
          pageSize={PAGE_SIZE}
          paginationMode={"server"}
          rowsPerPageOptions={[PAGE_SIZE]}
          sortingMode={"server"}
          sortModel={sortModel}
          filterMode={"server"}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          initialState={initialState}
          onSortModelChange={handleSortModelChange}
          getDetailPanelContent={isMobile && variant === "pending" ? getDetailPanelContent : undefined}
          getDetailPanelHeight={isMobile && variant === "pending" ? getDetailPanelHeight : undefined}
          detailPanelExpandedRowIds={detailPanelExpandedRowIds}
          sx={{
            flexGrow: 1,
            ...STANDARD_COLUMN_HEADER_TITLE_SX,
            ...(isMobile && variant === "pending"
              ? {
                  "& .MuiDataGrid-cell": {
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-detailPanel": {
                    backgroundColor: "transparent",
                    borderBottom: "1px solid",
                    borderBottomColor: "divider",
                  },
                  "& .MuiDataGrid-detailPanelToggleCell": {
                    backgroundColor: "blue",
                    display: "none",
                    width: 0,
                    minWidth: 0,
                    padding: 0,
                  },
                }
              : {}),
          }}
        />
      )}
    </DataTableContainer>
  );
};

export type NoticeSummaryTableVariant = "all" | "pending";

export interface NoticeSummaryTableProps {
  variant: NoticeSummaryTableVariant;
  onChangeVariant: (event: React.SyntheticEvent, newValue: NoticeSummaryTableVariant) => void;
  /** Distinct violation types applicable to this variant — see Notices.tsx, which owns the query so it survives the key={tab} remount. */
  applicableViolationTypes?: string[];
}

interface TableToolbarProps {
  tab: NoticeSummaryTableVariant;
  onChangeTab: (event: React.SyntheticEvent, newValue: NoticeSummaryTableVariant) => void;
  fetchTotalCount: () => Promise<number>;
  fetchDataBatch: (offset: number, limit: number) => Promise<unknown[]>;
  exportFileName: string;
  columns: GridColDef[];
  /** The table's default column visibility — see useHiddenColumnsCount's docs on why this must stay a stable, code-defined constant. */
  defaultVisibilityModel: GridColumnVisibilityModel;
  /** Below 700px, collapses the export button to icon-only. The filter button's label is controlled separately, at the parent DataGridPro's `localeText` prop. */
  isIconOnly: boolean;
  /** Clears this table's saved layout and restores its defaults — see DataTableContainerChildrenParams.resetLayout. */
  resetLayout: () => void;
  /**
   * Staleness banner for the Tow-Eligible column (AUT-5763). The poll gate
   * switches off on page 2+ and under any column filter — including the obvious
   * one, filtering to tow-eligible rows — so the row set stops refreshing, and
   * that needs stating where it is always visible rather than in a tooltip
   * nobody hovers. Rendered only when the column is on screen.
   *
   * The wording distinguishes the two, because they no longer freeze together:
   * the *row list* stops updating, but each dot's red/amber age comes off the
   * wall clock and keeps advancing regardless. Saying only "paused" invited the
   * reading that the dots had stopped ageing too.
   */
  isTowEligibleColumnShown: boolean;
  isAutoRefreshing: boolean;
  /** Last network confirmation, in the viewer's own clock — the right frame for
   * "how old is what I'm looking at". Per-row tooltips use the notice's timezone
   * instead, because those are evidence timestamps. */
  liveDataAsOf: Date | null;
}

const TableToolbar: React.FC<TableToolbarProps> = ({
  tab,
  onChangeTab,
  fetchTotalCount,
  fetchDataBatch,
  exportFileName,
  columns,
  defaultVisibilityModel,
  isIconOnly,
  resetLayout,
  isTowEligibleColumnShown,
  isAutoRefreshing,
  liveDataAsOf,
}) => {
  const apiRef = useGridApiContext();
  return (
    <Stack>
      <Tabs value={tab} variant="fullWidth" centered onChange={onChangeTab}>
        <Tab value="pending" label="Pending Notices" />
        <Tab value="all" label="All Notices" />
      </Tabs>
      {/* flexWrap + minHeight (not height): between the isMobile (450px) and isIconOnly (700px)
          breakpoints the button group is icon-only but the chip's "as of HH:MM:SS" tail still
          needs its full, unshrunk width (flexShrink: 0 above) — wider than the remaining space
          in that band. Wrapping the chip to its own line keeps the timestamp intact instead of
          letting it run off the container; a fixed height would clip that second line. */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        minHeight={52}
        px={1.25}
        py={0.5}
        rowGap={0.5}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <GridToolbarFilterButton
            componentsProps={{
              button: {
                sx: {
                  fontSize: "16px",
                  fontWeight: 400,
                  letterSpacing: 0.15,
                  textTransform: "none",
                  ...(isIconOnly ? { minWidth: "auto", px: 1, "& .MuiButton-startIcon": { mr: 0, ml: 0 } } : {}),
                },
              },
            }}
          />
          <ColumnsToolbarButton apiRef={apiRef} defaultVisibilityModel={defaultVisibilityModel} />
          <CustomExportButton
            getTotalCount={fetchTotalCount}
            fetchDataBatch={fetchDataBatch}
            fileName={exportFileName}
            columns={columns}
            maxRowsLimit={100000}
            aria-label="Export"
            buttonLabel={isIconOnly ? "" : "Export"}
            sx={{
              fontSize: "16px",
              fontWeight: 400,
              letterSpacing: 0.15,
              textTransform: "none",
              ...(isIconOnly ? { minWidth: "auto", px: 1, "& .MuiButton-startIcon": { mr: 0, ml: 0 } } : {}),
            }}
          />
          <ResetLayoutButton onReset={resetLayout} isIconOnly={isIconOnly} />
        </Stack>
        {isTowEligibleColumnShown && !isAutoRefreshing && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={
              liveDataAsOf
                ? `Row list paused — as of ${convertTimeToString(liveDataAsOf)}. Dots still age.`
                : "Row list paused. Dots still age."
            }
            // flexShrink: 0 — between the isMobile (450px) and isIconOnly (700px) breakpoints,
            // the button group is already icon-only, but this row has no flexWrap and
            // MuiChip-label truncates with an ellipsis by default. Without a shrink floor, a
            // width-constrained flex row would clip the "as of HH:MM:SS" tail, which AUT-5763
            // requires to stay load-bearing rather than decorative.
            sx={{ fontVariantNumeric: "tabular-nums", flexShrink: 0 }}
          />
        )}
      </Stack>
    </Stack>
  );
};

/**
 * Tow-eligibility for one notices-table row (AUT-5763).
 *
 * Thin on purpose: the rule itself lives in `resolveLiveSessionState`, shared
 * with the review page badge, so the queue and the notice detail agree about
 * the same notice. This adds only the two suppressions the table needs.
 *
 * **`Date.now()`, deliberately, not the last-confirmed stamp.** The column
 * answers two separable questions and they have different freshness needs:
 * *is a session still open* comes from `end_time` in the row data and does need
 * a fetch, but *how long has it been open* is derivable from `startTime` alone
 * and needs no network at all. Deriving the second from the confirmation stamp
 * froze the red→amber boundary the moment polling paused — and polling pauses
 * under any column filter, including filtering this very column to "Yes" to
 * build a dispatch list. A CV-2333 stale-open park would then have sat there
 * asserting "still in violation" indefinitely, in exactly the workflow the
 * amber state exists to protect. Reported by Lisa on #1355.
 *
 * `hasConfirmedData` false means no network response has completed yet — a
 * cached row must not claim presence at a time we never confirmed. A voided
 * notice is suppressed because there is nothing left to tow for; the badge
 * reads `voidedAt` for this, which the summary fragment doesn't select, so the
 * table uses the simple-status mapping it already relies on elsewhere.
 */
export const resolveTowEligibility = (
  row: Pick<EnforcementIntentSummary, "status" | "sessions">,
  hasConfirmedData: boolean,
): LiveSessionState => {
  if (!hasConfirmedData || enforcementIntentSimpleStatus[row.status] === "voided") {
    return { kind: "none" };
  }
  return resolveLiveSessionState(row.sessions, Date.now());
};

// Export-only: shown in the CSV via EXPORT_COLUMNS below, never in the on-screen grid or its
// Columns toggle panel. See AUT-5661.
const carrierColumn: GridColDef<EnforcementIntentSummary, string> = {
  field: "carrier",
  headerName: "Carrier",
  valueGetter({ row }) {
    return row.sessions?.[0]?.transaction?.account?.organizationName ?? "-";
  },
};

const PARAMS_BY_VARIANT = {
  pending: {
    defaultSortColumn: "issuedAt",
    defaultSortOrder: "asc",
  },
  all: {
    defaultSortColumn: "issuedAt",
    defaultSortOrder: "desc",
  },
};

export default NoticeSummaryTable;
